/**
 * Protocolo do gateway WebSocket.
 *
 * Desenho copiado do bonfire (Stoat) e do gateway do Discord, com as
 * lições já aplicadas:
 *   - o cliente recebe UM `Ready` com todo o estado inicial (evita a
 *     avalanche de N pedidos REST no arranque);
 *   - todos os eventos seguintes são deltas;
 *   - heartbeat com ack — sem ack, uma ligação zombie parece viva durante
 *     minutos e o utilizador não recebe mensagens sem saber porquê;
 *   - `seq` monotónico por sessão, para retomar após queda de rede em vez
 *     de refazer o Ready inteiro.
 */
export const GATEWAY_VERSION = 1;
/** Cliente → servidor. */
export var ClientOp;
(function (ClientOp) {
    ClientOp[ClientOp["Identify"] = 0] = "Identify";
    ClientOp[ClientOp["Heartbeat"] = 1] = "Heartbeat";
    ClientOp[ClientOp["Resume"] = 2] = "Resume";
    ClientOp[ClientOp["UpdatePresence"] = 3] = "UpdatePresence";
    ClientOp[ClientOp["BeginTyping"] = 4] = "BeginTyping";
    ClientOp[ClientOp["EndTyping"] = 5] = "EndTyping";
    /** Pede o próximo lote da lista de membros de um servidor grande. */
    ClientOp[ClientOp["RequestMembers"] = 6] = "RequestMembers";
    /** Actualiza o próprio estado de voz (mute/deaf/ecrã/câmara). */
    ClientOp[ClientOp["UpdateVoiceState"] = 7] = "UpdateVoiceState";
    /** Entra no escritório 2D de um servidor e pede o snapshot dos presentes. */
    ClientOp[ClientOp["EnterSpace"] = 8] = "EnterSpace";
    /** Envia a própria posição (throttled a ~10Hz pelo cliente). */
    ClientOp[ClientOp["UpdatePosition"] = 9] = "UpdatePosition";
    /** Sai do escritório 2D — despawn imediato para os outros. */
    ClientOp[ClientOp["LeaveSpace"] = 10] = "LeaveSpace";
    /** Tranca/destranca a porta da sala onde se está (ver `space.ts`). */
    ClientOp[ClientOp["LockRoom"] = 11] = "LockRoom";
    /** Bate à porta de uma sala trancada — só quem lá está dentro é notificado. */
    ClientOp[ClientOp["Knock"] = 12] = "Knock";
    /** Responde a quem bateu: aceitar destranca a porta, recusar só avisa. */
    ClientOp[ClientOp["AnswerKnock"] = 13] = "AnswerKnock";
    // O 14 era o `ReserveRoom`, retirado. Fica QUEIMADO de propósito: renumerar o
    // `Say`/`Poke` para aproveitar o buraco fazia um cliente antigo, ainda em
    // memória num separador aberto, mandar um "diz" que aqui chegava como outra
    // coisa qualquer. Um número perdido custa nada; a confusão custa caro.
    /** Balão de texto por cima do avatar — chat de proximidade, sem microfone. */
    ClientOp[ClientOp["Say"] = 15] = "Say";
    /** Chamar a atenção de UMA pessoa do escritório: um toque com destinatário. */
    ClientOp[ClientOp["Poke"] = 16] = "Poke";
    /** Ligar a UMA pessoa do escritório: toca do lado dela, esteja onde estiver no mapa. */
    ClientOp[ClientOp["Ligar"] = 17] = "Ligar";
    /** Quem recebe responde: aceitar abre a ligação, recusar termina-a. */
    ClientOp[ClientOp["AtenderLigacao"] = 18] = "AtenderLigacao";
    /** Cancelar (a tocar) ou desligar (em curso) — qualquer das duas pontas. */
    ClientOp[ClientOp["DesligarLigacao"] = 19] = "DesligarLigacao";
    /** Atirar uma bola de neve, a alguém ou para um ponto do jardim. */
    ClientOp[ClientOp["BolaDeNeve"] = 20] = "BolaDeNeve";
})(ClientOp || (ClientOp = {}));
/** Servidor → cliente. */
export var ServerOp;
(function (ServerOp) {
    ServerOp[ServerOp["Hello"] = 0] = "Hello";
    ServerOp[ServerOp["Ready"] = 1] = "Ready";
    ServerOp[ServerOp["HeartbeatAck"] = 2] = "HeartbeatAck";
    ServerOp[ServerOp["Event"] = 3] = "Event";
    /** A sessão morreu: `resumable` diz se vale a pena tentar Resume. */
    ServerOp[ServerOp["Invalidate"] = 4] = "Invalidate";
    ServerOp[ServerOp["Error"] = 5] = "Error";
    /**
     * O Resume pegou: a sessão antiga continua, e o que faltava já foi entregue.
     *
     * Existe porque um resume LIMPO — nada em atraso — não tinha resposta nenhuma:
     * o servidor replicava zero eventos e ficava calado, e o cliente ficava eternamente
     * "a retomar" com uma ligação perfeitamente viva por baixo. Silêncio não é
     * confirmação; quem retoma tem de o ouvir dizer.
     */
    ServerOp[ServerOp["Resumed"] = 6] = "Resumed";
})(ServerOp || (ServerOp = {}));
/** Códigos de fecho. 4xxx = específicos da aplicação. */
export var GatewayCloseCode;
(function (GatewayCloseCode) {
    GatewayCloseCode[GatewayCloseCode["UnknownError"] = 4000] = "UnknownError";
    GatewayCloseCode[GatewayCloseCode["UnknownOpcode"] = 4001] = "UnknownOpcode";
    GatewayCloseCode[GatewayCloseCode["DecodeError"] = 4002] = "DecodeError";
    GatewayCloseCode[GatewayCloseCode["NotAuthenticated"] = 4003] = "NotAuthenticated";
    GatewayCloseCode[GatewayCloseCode["AuthenticationFailed"] = 4004] = "AuthenticationFailed";
    GatewayCloseCode[GatewayCloseCode["AlreadyAuthenticated"] = 4005] = "AlreadyAuthenticated";
    GatewayCloseCode[GatewayCloseCode["InvalidSequence"] = 4007] = "InvalidSequence";
    GatewayCloseCode[GatewayCloseCode["RateLimited"] = 4008] = "RateLimited";
    GatewayCloseCode[GatewayCloseCode["SessionTimedOut"] = 4009] = "SessionTimedOut";
    GatewayCloseCode[GatewayCloseCode["InvalidVersion"] = 4012] = "InvalidVersion";
})(GatewayCloseCode || (GatewayCloseCode = {}));
/** Fechos em que reconectar do zero é inútil (credenciais/versão erradas). */
export const NON_RESUMABLE_CLOSE_CODES = new Set([
    GatewayCloseCode.AuthenticationFailed,
    GatewayCloseCode.NotAuthenticated,
    GatewayCloseCode.InvalidVersion,
]);
export const HEARTBEAT_INTERVAL_MS = 20_000;
/** Sem ack neste prazo, a ligação está morta — reconectar. */
export const HEARTBEAT_TIMEOUT_MS = 45_000;
/** Janela para retomar com Resume antes de o servidor deitar fora o buffer. */
export const SESSION_RESUME_WINDOW_MS = 120_000;
/** Eventos guardados por sessão para replay no Resume. */
export const RESUME_BUFFER_SIZE = 512;
/** O typing expira sozinho — nunca confiar num EndTyping que pode não chegar. */
export const TYPING_TTL_MS = 8_000;
/**
 * Mudar de estado é um clique num menu: uma vez por segundo é folgado para uma
 * pessoa e apertado para um script. Sem guarda nenhuma, cada mudança é
 * amplificada — uma publicação por servidor partilhado, e daí um evento para
 * cada sessão de cada membro. É o op mais barato de enviar e o mais caro de
 * entregar, que é exactamente a forma de um amplificador.
 */
export const PRESENCE_MIN_INTERVAL_MS = 1_000;
/** Tamanho máximo de um balão de texto. O servidor corta, não rejeita. */
export const SAY_MAX_LENGTH = 140;
/** O balão desaparece sozinho — não há "apagar", tal como no typing. */
export const SAY_TTL_MS = 7_000;
/** Intervalo mínimo entre balões, imposto no servidor. */
export const SAY_MIN_INTERVAL_MS = 700;
/**
 * O chat do escritório guarda só o bocado recente — é conversa de sala, não um
 * canal. Cinquenta frases e doze horas: quem chega de manhã vê o que se disse
 * ontem à tarde, e nada fica para sempre.
 */
export const SPACE_CHAT_HISTORY = 50;
export const SPACE_CHAT_TTL_SECONDS = 12 * 60 * 60;
/**
 * Chamar a atenção faz um BARULHÃO na máquina do outro, por isso a guarda é a
 * mais apertada de todas: cinco segundos. Um "toque" que se pode repetir em
 * rajada deixa de ser um toque e passa a ser uma arma — e a vítima não tem
 * botão nenhum para o desligar sem sair do escritório.
 */
export const POKE_MIN_INTERVAL_MS = 5_000;
/** Entre duas bolas de neve da mesma pessoa — dá para uma guerra, não para uma metralhadora. */
export const BOLA_DE_NEVE_MIN_INTERVAL_MS = 700;
/** Até onde chega uma bola de neve, em px (7 tiles). */
export const BOLA_DE_NEVE_ALCANCE_PX = 224;
/** Quanto tempo uma ligação privada toca antes de contar como não atendida. */
export const LIGACAO_TOQUE_MS = 30_000;
/** Intervalo mínimo entre duas ligações feitas pela mesma sessão — ligar não é spam. */
export const LIGAR_MIN_INTERVAL_MS = 3_000;
/** Quanto tempo o avatar estremece. Puramente visual, morre sozinho. */
export const POKE_TTL_MS = 1_200;
/** Bater à porta de segundo a segundo seria uma campainha estragada. */
export const KNOCK_MIN_INTERVAL_MS = 3_000;
/** Um pedido para entrar que ninguém atende cai por si. */
export const KNOCK_TTL_MS = 30_000;
/**
 * Canais de pub/sub no Redis. O gateway não fala directamente com outras
 * instâncias: publica aqui e cada instância reenvia aos sockets que tem.
 * É isto que permite escalar o gateway horizontalmente.
 */
export const Topic = {
    server: (id) => `gv:server:${id}`,
    channel: (id) => `gv:channel:${id}`,
    user: (id) => `gv:user:${id}`,
    /** Escritório 2D. O `psubscribe("gv:*")` já cobre isto — zero config nova. */
    space: (id) => `gv:space:${id}`,
};
//# sourceMappingURL=events.js.map